import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Home } from '../../icons';
import { NAVIGATION_CONFIG, NavSection } from '../../data/staticData';
import { useThemeContext } from '../../context/ThemeContext';
import * as Icons from '../../icons';

interface MobileNavProps {
    isOpen: boolean;
    onClose: () => void;
}

const getIcon = (iconName: string) => {
    const IconComponent = (Icons as Record<string, React.FC<{ className?: string }>>)[iconName];
    return IconComponent || Icons.FileText;
};

const MobileNav: React.FC<MobileNavProps> = ({ isOpen, onClose }) => {
    const { isDarkMode } = useThemeContext();
    const [expandedSection, setExpandedSection] = useState<string | null>(null);

    const toggleSection = (sectionId: string) => {
        setExpandedSection(expandedSection === sectionId ? null : sectionId);
    };

    if (!isOpen) return null;

    return (
        <div className={`xl:hidden absolute top-full left-0 w-full z-40 shadow-xl border-t ${isDarkMode
            ? 'bg-slate-900/95 backdrop-blur-md border-white/10'
            : 'bg-white/95 backdrop-blur-md border-gray-100'
            }`}>
            <div className="max-w-7xl mx-auto px-4 py-4 max-h-[70vh] overflow-y-auto">
                <nav className="space-y-1" role="navigation" aria-label="Mobile navigation">
                    <Link
                        to="/"
                        onClick={onClose}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isDarkMode
                            ? 'text-gray-300 hover:text-white hover:bg-white/10'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                    >
                        <Home className="w-5 h-5" aria-hidden="true" />
                        <span>Home</span>
                    </Link>

                    {NAVIGATION_CONFIG.map((section: NavSection) => {
                        const SectionIcon = getIcon(section.icon);
                        const isExpanded = expandedSection === section.id;

                        // Direct link (no dropdown) if section has path and no items
                        if (section.path && section.items.length === 0) {
                            return (
                                <Link
                                    key={section.id}
                                    to={section.path}
                                    onClick={onClose}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isDarkMode
                                        ? 'text-gray-300 hover:text-white hover:bg-white/10'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                        }`}
                                >
                                    <SectionIcon className="w-5 h-5" aria-hidden="true" />
                                    <span>{section.label}</span>
                                </Link>
                            );
                        }

                        return (
                            <div key={section.id} className="space-y-1">
                                <button
                                    onClick={() => toggleSection(section.id)}
                                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isDarkMode
                                        ? 'text-gray-300 hover:text-white hover:bg-white/10'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                        } ${isExpanded ? (isDarkMode ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900') : ''}`}
                                    aria-expanded={isExpanded}
                                >
                                    <div className="flex items-center gap-3">
                                        <SectionIcon className="w-5 h-5" aria-hidden="true" />
                                        <span>{section.label}</span>
                                    </div>
                                    <ChevronDown
                                        className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                                        aria-hidden="true"
                                    />
                                </button>

                                {isExpanded && (
                                    <div className={`ml-4 pl-4 border-l-2 space-y-1 ${isDarkMode ? 'border-white/20' : 'border-gray-200'
                                        }`}>
                                        {section.items.map((item) => {
                                            const ItemIcon = getIcon(item.icon);
                                            return (
                                                <Link
                                                    key={item.id}
                                                    to={item.path}
                                                    onClick={onClose}
                                                    className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${isDarkMode
                                                        ? 'text-gray-400 hover:text-white hover:bg-white/5'
                                                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                                                        }`}
                                                >
                                                    <ItemIcon className="w-4 h-4" aria-hidden="true" />
                                                    <span>{item.label}</span>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </nav>
            </div>
        </div>
    );
};

export default MobileNav;
